let configPromise = null;
const sdkPromises = new Map();

function authHeaders(session) {
  return session?.access_token ? { authorization: `Bearer ${session.access_token}` } : {};
}

async function parseResponse(response) {
  let payload = null;
  try { payload = await response.json(); } catch (_) {}
  if (!response.ok) {
    const error = new Error(payload?.error || payload?.message || `Request failed with ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload || {};
}

export function loadCommerceConfig() {
  if (!configPromise) {
    configPromise = fetch('/api/v3-commerce-config', { headers: { accept: 'application/json' }, cache: 'no-store' })
      .then(parseResponse)
      .catch((error) => {
        configPromise = null;
        throw error;
      });
  }
  return configPromise;
}

export async function loadPayPalSdk({ intent = 'subscription' } = {}) {
  const config = await loadCommerceConfig();
  const mode = intent === 'capture' ? 'capture' : 'subscription';
  const namespace = mode === 'capture' ? 'watchvimPayPalCheckout' : 'watchvimPayPalSubscription';
  if (typeof window !== 'undefined' && window[namespace]) return window[namespace];
  if (sdkPromises.has(namespace)) return sdkPromises.get(namespace);

  const promise = new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      'client-id': config.paypal_client_id,
      currency: config.currency || 'USD',
      components: 'buttons',
      intent: mode,
    });
    if (mode === 'subscription') params.set('vault', 'true');

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
    script.async = true;
    script.dataset.namespace = namespace;
    script.setAttribute('data-namespace', namespace);
    script.onload = () => {
      const sdk = window[namespace];
      if (!sdk) reject(new Error('PayPal SDK loaded without an available namespace.'));
      else resolve(sdk);
    };
    script.onerror = () => reject(new Error('Unable to load PayPal checkout.'));
    document.head.appendChild(script);
  }).catch((error) => {
    sdkPromises.delete(namespace);
    throw error;
  });

  sdkPromises.set(namespace, promise);
  return promise;
}

export async function activateMembership({ session, subscriptionId, plan, paypalPlanId, previousSubscriptionId = '' }) {
  const response = await fetch('/api/subscription/create', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...authHeaders(session) },
    body: JSON.stringify({
      subscriptionID: subscriptionId,
      plan,
      paypalPlanId,
      previousSubscriptionID: previousSubscriptionId || undefined,
      prevSub: previousSubscriptionId || undefined,
    }),
  });
  return parseResponse(response);
}

export async function cancelMembership({ session, subscriptionId, plan = '', reason = 'User requested cancellation from WatchVIM V3.' }) {
  if (!session?.user || !subscriptionId) throw new Error('An active signed-in membership is required to cancel renewal.');
  const response = await fetch('/api/subscription/cancel', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...authHeaders(session) },
    body: JSON.stringify({ subscription_id: subscriptionId, plan: plan || undefined, reason }),
  });
  return parseResponse(response);
}

export async function createTvodOrder({ session, catalogTitleId, rentalScope = 'title', episodeId = '', seasonNumber = '' }) {
  if (!session?.user) throw new Error('Log in before renting WatchVIM content.');
  const response = await fetch('/api/tvod/order/create', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...authHeaders(session) },
    body: JSON.stringify({ catalogTitleId, rentalScope, episodeId: episodeId || undefined, seasonNumber: seasonNumber || undefined }),
  });
  return parseResponse(response);
}

export async function persistTvodEntitlement({ session, orderId, titleId, title, purchaseType = 'tvod_rental', rentalWindowHours = 48, rentalScope = 'title', parentTitleId = '', seasonNumber = '', episodeId = '', amount, currency = 'USD' }) {
  if (!session?.user) throw new Error('Log in before saving WatchVIM rental access.');
  const response = await fetch('/api/tvod/create', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...authHeaders(session) },
    body: JSON.stringify({
      orderID: orderId,
      titleId,
      title,
      purchaseType,
      rentalWindowHours,
      rentalScope,
      parentTitleId: parentTitleId || undefined,
      seasonNumber: seasonNumber || undefined,
      episodeId: episodeId || undefined,
      amount,
      currency,
    }),
  });
  return parseResponse(response);
}
